<template>
    <div class="setup-container">
        <div class="page-hero">
            <div>
                <p class="eyebrow">Permissions</p>
                <h1>Create Role</h1>
                <p class="hero-copy">Add new access roles and keep track of the role catalog in one place.</p>
            </div>
            <button class="setup-btn" @click="loadRoles">
                <RefreshCcw class="icon" />
                Refresh
            </button>
        </div>

        <section class="panel">
            <form class="setup-form" @submit.prevent="submitForm">

                <div class="form-group">
                    <label for="roleName">Role name</label>
                    <select id="roleName" v-model="form.roleName" required>
                        <option value="" disabled>Select role name</option>
                        <option value="admin">Admin</option>
                        <option value="subadmin">Sub Admin</option>
                        <option value="member">Member</option>
                        <option value="sponsor">Sponsor</option>
                        <option value="executive">Executive</option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea
                        id="description"
                        v-model="form.description"
                        rows="4"
                        placeholder="Describe what this role can access"
                    />
                </div>

                <div class="buttons mt-3">
                    <button type="submit">Create Role</button> &nbsp;
                    <button type="button" @click="resetForm">Reset</button>
                </div>
            </form>
        </section>

        <section class="panel">
            <div class="header-search">
                <div>
                    <p class="section-kicker">Role list</p>
                    <h2>Existing roles</h2>
                </div>
                <div class="search-bar">
                    <Search />
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search roles..."
                    />
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Role</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedRoles.length === 0">
                        <td colspan="4" class="no-data">No roles found.</td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedRoles" :key="item.RoleId ?? index">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ item.RoleName }}</td>
                        <td>{{ item.Description || "-" }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="pagination">
                <button @click="prevPage" :disabled="currentPage === 1">
                    <ChevronLeft />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: currentPage === page }"
                >
                    {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages">
                    <ChevronRight />
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, RefreshCcw, Search } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";

const apiListUrl = "/roles_lists.php";
const apiCreateUrl = "/roles_setup.php";

const roles = ref([]);
const searchQuery = ref("");
const currentPage = ref(1);
const pageSize = 8;

const form = ref({
    roleId: "",
    roleName: "",
    description: ""
});

const filteredRoles = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return roles.value;

    return roles.value.filter((item) => {
        return (
            String(item.RoleName || "").toLowerCase().includes(query) ||
            String(item.Description || "").toLowerCase().includes(query)
        );
    });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredRoles.value.length / pageSize)));

const paginatedRoles = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredRoles.value.slice(start, start + pageSize);
});

watch(searchQuery, () => {
    currentPage.value = 1;
});

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) currentPage.value = page;
}

function prevPage() {
    if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++;
}

function resetForm() {
    form.value.roleName = "";
    form.value.description = "";
}

async function loadRoles() {
    try {
        const response = await axios.get(apiListUrl);
        roles.value = response?.data?.data ?? [];
    } catch (error) {
        console.error(error);
    }
}

async function submitForm() {
    try {
        const payload = new FormData();
        payload.append("RoleName", form.value.roleName);
        payload.append("Description", form.value.description);

        const response = await axios.post(apiCreateUrl, payload, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        alert(response?.data?.message || "Role created successfully.");
        resetForm();
        await loadRoles();
    } catch (error) {
        alert(error?.response?.data?.message || error.message);
    }
}

onMounted(loadRoles);
</script>
